import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Modal,
  ModalContent,
  useDisclosure,
} from '@nextui-org/react'
import { useEffect, useState } from 'react'
import { HiCube, HiLogout, HiMenu, HiPlus, HiChevronDown } from 'react-icons/hi'
import { HiArrowsRightLeft, HiOutlineArrowDownOnSquare, HiOutlineArrowUpOnSquare, HiInbox } from 'react-icons/hi2'
import { logout } from '../api/axiosAuthorization'
import { AddGroup } from '../components/addgroup'
import { SelectUngroupedTanks, SelectGroupForAssign } from '../components/assignGroup'
import { SelectGroupForRemove, SelectTanksToRemove } from '../components/removeGroup'
import { InitialInfo as GradeInitialInfo } from '../components/grade/InitialInfo'
import { SelectTanksGrade } from '../components/grade/SelectTanksGrade'
import EmptyCardContainer from '../components/home/EmptyCardContainer'
import GroupCardContainer from '../components/home/GroupCardContainer'
import UngroupedCardContainer from '../components/home/UngroupedCardContainer'
import { InitialInfo as StockInitialInfo } from '../components/stock/InitialInfo'
import { SelectTanksStock } from '../components/stock/SelectTanksStock'
import SelectTank from './SelectTank'
import { Update, UpdateCTA } from '../components/update'
import { useGroupStore } from '../store/group/useGroupStore'
import { useTankStore } from '../store/tank/useTankStore'
import useHomeAssignGroupStore from '../store/home/useHomeAssignGroupStore'
import useHomeGradeStore from '../store/home/useHomeGradeStore'
import useHomeStockStore from '../store/home/useHomeStockStore'
import useHomeRemoveGroupStore from '../store/home/useHomeRemoveGroupStore'
import { ConfirmEmptyTank } from '../components/emptyTank/ConfirmEmptyTank'
import type { SelectedTankArray } from '@/types/view'

export default function Home() {
  const groupsWithDetails = useGroupStore((state) => {
    return state.groupsWithDetails;
  })
  const releasedGroups = useGroupStore((state) => state.releasedGroups)
  const emptyTanks = useTankStore((state) => state.emptyTanks)

  // Mobile dropdown handlers - First modals
  const {
    isOpen: isGradeOpen,
    onOpen: onGradeOpen,
    onOpenChange: onGradeOpenChange,
  } = useDisclosure()
  const {
    isOpen: isStockOpen,
    onOpen: onStockOpen,
    onOpenChange: onStockOpenChange,
  } = useDisclosure()

  // Mobile dropdown handlers - Second modals (InitialInfo)
  const {
    isOpen: isGradeInitialInfoOpen,
    onOpen: onGradeInitialInfoOpen,
    onOpenChange: onGradeInitialInfoOpenChange,
  } = useDisclosure()
  const {
    isOpen: isStockInitialInfoOpen,
    onOpen: onStockInitialInfoOpen,
    onOpenChange: onStockInitialInfoOpenChange,
  } = useDisclosure()

  // Mobile dropdown handlers - AddGroup modal
  const {
    isOpen: isAddGroupOpen,
    onOpen: onAddGroupOpen,
    onOpenChange: onAddGroupOpenChange,
  } = useDisclosure()

  // Mobile dropdown handlers - AssignGroup modals
  const {
    isOpen: isAssignUngroupedOpen,
    onOpen: onAssignUngroupedOpen,
    onOpenChange: onAssignUngroupedOpenChange,
  } = useDisclosure()
  const {
    isOpen: isAssignGroupOpen,
    onOpen: onAssignGroupOpen,
    onOpenChange: onAssignGroupOpenChange,
  } = useDisclosure()

  // Mobile dropdown handlers - RemoveGroup modals
  const {
    isOpen: isRemoveGroupSelectOpen,
    onOpen: onRemoveGroupSelectOpen,
    onOpenChange: onRemoveGroupSelectOpenChange,
  } = useDisclosure()
  const {
    isOpen: isRemoveTanksOpen,
    onOpen: onRemoveTanksOpen,
    onOpenChange: onRemoveTanksOpenChange,
  } = useDisclosure()

  // Mobile dropdown handlers - EmptyTank modals
  const {
    isOpen: isEmptyTankSelectOpen,
    onOpen: onEmptyTankSelectOpen,
    onOpenChange: onEmptyTankSelectOpenChange,
    onClose: onEmptyTankSelectClose,
  } = useDisclosure()
  const {
    isOpen: isEmptyTankConfirmOpen,
    onOpen: onEmptyTankConfirmOpen,
    onOpenChange: onEmptyTankConfirmOpenChange,
  } = useDisclosure()
  const [emptyTankSelectedTank, setEmptyTankSelectedTank] = useState<SelectedTankArray[0] | null>(null)

  // Store resets
  const { reset: resetGrade } = useHomeGradeStore()
  const { reset: resetStock } = useHomeStockStore()
  const { reset: resetAssign } = useHomeAssignGroupStore()
  const { reset: resetRemove } = useHomeRemoveGroupStore()

  // Reset stores when both modals are closed
  useEffect(() => {
    if (!isGradeOpen && !isGradeInitialInfoOpen) {
      resetGrade()
    }
  }, [isGradeOpen, isGradeInitialInfoOpen, resetGrade])

  useEffect(() => {
    if (!isStockOpen && !isStockInitialInfoOpen) {
      resetStock()
    }
  }, [isStockOpen, isStockInitialInfoOpen, resetStock])

  useEffect(() => {
    if (!isAssignUngroupedOpen && !isAssignGroupOpen) {
      resetAssign()
    }
  }, [isAssignUngroupedOpen, isAssignGroupOpen, resetAssign])

  useEffect(() => {
    if (!isRemoveGroupSelectOpen && !isRemoveTanksOpen) {
      resetRemove()
    }
  }, [isRemoveGroupSelectOpen, isRemoveTanksOpen, resetRemove])

  // NEW: Create Group card blocks instead of Batch blocks
  // Defensive check: ensure groupsWithDetails is an array before filtering
  const createGroupBlocks = (Array.isArray(groupsWithDetails) ? groupsWithDetails : [])
    .map((group) => {
      const items = group.items

      // Only render groups with valid items
      if (!items || !Array.isArray(items) || items.length === 0) {
        return null
      }

      return <GroupCardContainer key={group.id} group={group} />
    })
    .filter((block) => block !== null) // Remove null blocks

  // Ungrouped tanks: tanks with fish but not assigned to any group (from released groups)
  const ungroupedTankIds = (Array.isArray(releasedGroups) ? releasedGroups : [])
    .flatMap((group) => group.items || [])
    .map((item) => item.tank?.id)
    .filter((id): id is string => !!id)

  // Empty tanks: tanks without fish (from empty tanks API)
  const emptyTankIds = (Array.isArray(emptyTanks) ? emptyTanks : [])
    .map((tank) => tank.id)

  const handleLogout = () => {
    logout()
  }

  // Mobile dropdown handlers
  const handleGradeClick = () => {
    resetGrade()
    onGradeOpen()
  }

  const handleStockClick = () => {
    resetStock()
    onStockOpen()
  }

  const handleAssignClick = () => {
    resetAssign()
    onAssignUngroupedOpen()
  }

  const handleRemoveClick = () => {
    resetRemove()
    onRemoveGroupSelectOpen()
  }

  const handleEmptyTankSelect = (tankSelectedList: SelectedTankArray) => {
    if (tankSelectedList.length === 0) return

    const tank = tankSelectedList[0]
    onEmptyTankSelectClose()
    setTimeout(() => {
      setEmptyTankSelectedTank(tank)
      onEmptyTankConfirmOpen()
    }, 50)
  }

  const handleEmptyTankSuccess = () => {
    setEmptyTankSelectedTank(null)
  }

  return (
    <div className="px-1 pt-0 pb-20 md:p-0 max-w">
      <div className="md:space-y-6 lg:space-y-0">
        <div className="md:hidden space-y-3">
          <header className="grid grid-cols-[1fr_auto] items-center gap-4">
            <h1 className="font-bold text-tdplindigo text-2xl">Home</h1>
            <Dropdown placement="bottom-end">
              <DropdownTrigger>
                <Button
                  isIconOnly
                  variant="light"
                  size="sm"
                  className="text-tdplindigo hover:bg-tdplindigo/10"
                >
                  <HiMenu className="w-5 h-5" />
                </Button>
              </DropdownTrigger>
              <DropdownMenu aria-label="Quick actions" className="w-32">
                <DropdownItem
                  key="grade"
                  className="custom-dropdown-item"
                  startContent={<HiArrowsRightLeft className="w-4 h-4" />}
                  onPress={handleGradeClick}
                >
                  Grade
                </DropdownItem>
                <DropdownItem
                  key="stock"
                  className="custom-dropdown-item"
                  startContent={<HiCube className="w-4 h-4" />}
                  onPress={handleStockClick}
                >
                  Stock
                </DropdownItem>
                <DropdownItem
                  key="addgroup"
                  className="custom-dropdown-item"
                  startContent={<HiPlus className="w-4 h-4" />}
                  onPress={onAddGroupOpen}
                >
                  Add Group
                </DropdownItem>
                <DropdownItem
                  key="assigngroup"
                  className="custom-dropdown-item"
                  startContent={<HiOutlineArrowDownOnSquare className="w-4 h-4" />}
                  onPress={handleAssignClick}
                >
                  Assign
                </DropdownItem>
                <DropdownItem
                  key="removegroup"
                  className="custom-dropdown-item"
                  startContent={<HiOutlineArrowUpOnSquare className="w-4 h-4" />}
                  onPress={handleRemoveClick}
                >
                  Remove
                </DropdownItem>
                <DropdownItem
                  key="emptytank"
                  className="custom-dropdown-item"
                  startContent={<HiInbox className="w-4 h-4" />}
                  onPress={onEmptyTankSelectOpen}
                >
                  Empty
                </DropdownItem>
                <DropdownItem
                  key="logout"
                  className="custom-dropdown-item text-danger"
                  color="danger"
                  startContent={<HiLogout className="w-4 h-4" />}
                  onPress={handleLogout}
                >
                  Logout
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </header>
        </div>

        <div className="hidden md:flex md:flex-row md:justify-between gap-0 md:items-center">
          <div className="flex items-center w-full md:w-auto">
            <h1 className="font-bold text-tdplindigo text-[2rem]">Home</h1>
          </div>
          <div className="flex md:justify-evenly gap-2">
            {/* Update - Standalone Button */}
            <Update />

            {/* Tank Dropdown */}
            <Dropdown placement="bottom-end" classNames={{ content: "min-w-0 w-[120px]" }}>
              <DropdownTrigger>
                <Button
                  className="font-bold bg-tdplbisque text-tdplprussian text-[1.25rem] hover:bg-tdpllightgrey hover:text-tdplindigo min-w-[120px]"
                  endContent={<HiChevronDown className="w-4 h-4" />}
                >
                  Tank
                </Button>
              </DropdownTrigger>
              <DropdownMenu aria-label="Tank actions">
                <DropdownItem
                  key="stock"
                  onPress={handleStockClick}
                  className="dropdown-item-center"
                >
                  Stock
                </DropdownItem>
                <DropdownItem
                  key="grade"
                  onPress={handleGradeClick}
                  className="dropdown-item-center"
                >
                  Grade
                </DropdownItem>
                <DropdownItem
                  key="emptytank"
                  onPress={onEmptyTankSelectOpen}
                  className="dropdown-item-center"
                >
                  Empty
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>

            {/* Group Dropdown */}
            <Dropdown placement="bottom-end" classNames={{ content: "min-w-0 w-[120px]" }}>
              <DropdownTrigger>
                <Button
                  className="font-bold bg-tdplbisque text-tdplprussian text-[1.25rem] hover:bg-tdpllightgrey hover:text-tdplindigo min-w-[120px]"
                  endContent={<HiChevronDown className="w-4 h-4" />}
                >
                  Group
                </Button>
              </DropdownTrigger>
              <DropdownMenu aria-label="Group actions">
                <DropdownItem
                  key="addgroup"
                  onPress={onAddGroupOpen}
                  className="dropdown-item-center"
                >
                  Add
                </DropdownItem>
                <DropdownItem
                  key="assigngroup"
                  onPress={handleAssignClick}
                  className="dropdown-item-center"
                >
                  Assign
                </DropdownItem>
                <DropdownItem
                  key="removegroup"
                  onPress={handleRemoveClick}
                  className="dropdown-item-center"
                >
                  Remove
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </div>
        </div>
      </div>

      {/* Floating Update button for mobile */}
      <UpdateCTA />
      <div>{createGroupBlocks}</div>
      <div>
        <UngroupedCardContainer ungroupedTankIds={ungroupedTankIds} />
      </div>
      <div>
        <EmptyCardContainer emptyTankIds={emptyTankIds} />
      </div>

      {/* Shared Modals (Mobile + Desktop) */}
      {/* Grade Modals */}
      <SelectTanksGrade
        isOpen={isGradeOpen}
        onOpenChange={onGradeOpenChange}
        onOpen={onGradeInitialInfoOpen}
      />
      <GradeInitialInfo
        isOpen={isGradeInitialInfoOpen}
        onOpenChange={onGradeInitialInfoOpenChange}
        isBack={onGradeOpen}
      />

      {/* Stock Modals */}
      <SelectTanksStock
        isOpen={isStockOpen}
        onOpenChange={onStockOpenChange}
        onOpen={onStockInitialInfoOpen}
      />
      <StockInitialInfo
        isOpen={isStockInitialInfoOpen}
        onOpenChange={onStockInitialInfoOpenChange}
        isBack={onStockOpen}
      />

      {/* Add Group Modal */}
      <AddGroup
        isOpen={isAddGroupOpen}
        onOpenChange={onAddGroupOpenChange}
        showButton={false}
      />

      {/* Assign Group Modals */}
      <SelectUngroupedTanks
        isOpen={isAssignUngroupedOpen}
        onOpenChange={onAssignUngroupedOpenChange}
        onOpen={onAssignGroupOpen}
      />
      <SelectGroupForAssign
        isOpen={isAssignGroupOpen}
        onOpenChange={onAssignGroupOpenChange}
        isBack={onAssignUngroupedOpen}
      />

      {/* Remove Group Modals */}
      <SelectGroupForRemove
        isOpen={isRemoveGroupSelectOpen}
        onOpenChange={onRemoveGroupSelectOpenChange}
        onOpen={onRemoveTanksOpen}
      />
      <SelectTanksToRemove
        isOpen={isRemoveTanksOpen}
        onOpenChange={onRemoveTanksOpenChange}
        isBack={onRemoveGroupSelectOpen}
      />

      {/* Empty Tank Modals */}
      <Modal
        isOpen={isEmptyTankSelectOpen}
        onOpenChange={onEmptyTankSelectOpenChange}
        isDismissable={true}
        isKeyboardDismissDisabled={false}
        size="3xl"
        scrollBehavior="inside"
        data-testid="empty-tank-select-modal"
        classNames={{
          wrapper: 'z-[1400]',
          backdrop: 'z-[1300]',
          base: 'max-h-[85vh] sm:max-h-[90vh] md:max-h-[95vh]',
        }}
      >
        <ModalContent className="overflow-x-hidden">
          {(onCloseModal) => (
            <SelectTank
              submitTankArray={handleEmptyTankSelect}
              groupIds={['$ungrouped-zero-count']}
              onClose={onCloseModal}
              limit={1}
              submitButtonText="Continue"
              singleSelect={true}
            />
          )}
        </ModalContent>
      </Modal>
      <ConfirmEmptyTank
        isOpen={isEmptyTankConfirmOpen}
        onOpenChange={onEmptyTankConfirmOpenChange}
        isBack={onEmptyTankSelectOpen}
        selectedTank={emptyTankSelectedTank}
        onSuccess={handleEmptyTankSuccess}
      />
    </div>
  )
}
